import type { LearnerState } from '@/types/learner';
import type { SessionRecord } from '@/types/session';
import { PERSONAS_SEED } from '@/seed/personas';
import { COACH } from '@/constants/coach';

/**
 * Compose the coach hero content for a session's debrief. Pure function so
 * the copy can be iterated without touching the screen. Per brief §4.4 the
 * headline is personal to the session; per §4.4 failed-session variant the
 * headline reframes toward learning.
 *
 * The body is grounded in actual session data — moment counts, score delta
 * vs the prior attempt, and (when available) a direct quote of the rep's
 * strongest moment. No more boilerplate "Two moments stood out — and one
 * lost you the room" regardless of what happened.
 */

export interface DebriefHero {
  outcome_label: string;
  outcome_tone: 'win' | 'warn';
  score: number;
  eyebrow: string;
  headline: string;
  body: string;
}

export function composeDebriefHero(session: SessionRecord, learner: LearnerState): DebriefHero {
  const persona = PERSONAS_SEED[session.persona_id];
  const first = persona.display_name.split(' ')[0];
  const { possessive, subject } = persona.pronouns;
  const prior = priorAttempt(session, learner);

  const wins = session.moments.filter((m) => m.type === 'win');
  const growths = session.moments.filter((m) => m.type === 'growth');

  // Score delta vs the previous attempt against the same persona, if any.
  // Computed up here so closed-and-soft-yes branches can both use it.
  let priorBit = '';
  if (prior) {
    if (session.score > prior.score) {
      priorBit = ` Score ${session.score}, up from ${prior.score} last time.`;
    } else if (session.score < prior.score) {
      priorBit = ` Score ${session.score}, down from ${prior.score} last time.`;
    } else {
      priorBit = ` Score held at ${session.score}.`;
    }
  } else {
    priorBit = ` Score ${session.score} on a first attempt.`;
  }

  if (session.outcome === 'closed') {
    // A "soft yes" — the persona agreed but the meta avatar flagged the
    // run as teachable (more growth moments than wins). Most common with
    // Kayla when reps take her "skip ahead" bait: she says yes too easily,
    // the meeting's on the books but it tends to slip. Score reflects this
    // independently; this just flags it in the headline so reps don't read
    // a thin yes as a real win.
    const softYes = wins.length === 0 || growths.length >= wins.length;

    if (softYes) {
      return {
        outcome_label: 'SOFT YES',
        outcome_tone: 'warn',
        score: session.score,
        eyebrow: `${COACH.title} · your debrief`,
        headline: `${first} said yes — but it was a soft one.`,
        body:
          growths.length > 0
            ? `${growths.length} moment${growths.length === 1 ? '' : 's'} flagged for growth — that's where the yes got thin. The meeting's on the books, but this kind of yes tends to no-show or reschedule. Walk through ${growths.length === 1 ? 'it' : 'them'} with me; the next attempt will land differently.${priorBit}`
            : `${first} agreed, but discovery didn't go deep enough to anchor the proof. The meeting's on the books — but this kind of yes tends to slip. Let's tighten the next attempt.${priorBit}`,
      };
    }

    const topWin = wins[0];
    return {
      outcome_label: 'DEAL CLOSED',
      outcome_tone: 'win',
      score: session.score,
      eyebrow: `${COACH.title} · your debrief`,
      headline: `You closed ${first} — the right way.`,
      body: topWin
        ? `The turn that did it: "${truncate(topWin.quote, 90)}" That's how you anchor proof to ${possessive} reality. The next persona will push harder — let's get you ready.`
        : `Score ${session.score}. ${first} signed off because you stayed in ${possessive} world the whole time. The next persona will push harder — let's get you ready.`,
    };
  }

  // ---- Not closed: ground the headline + body in counts and deltas. -----

  // Headline reflects the trajectory, not a fixed phrase.
  let headline: string;
  if (prior && session.score > prior.score) {
    headline = `You didn't close ${first} — but you got further than last time.`;
  } else if (prior && session.score < prior.score) {
    headline = `You didn't close ${first} — and you slipped from last time.`;
  } else if (prior) {
    headline = `You didn't close ${first} — same ground as last time.`;
  } else if (wins.length > 0) {
    headline = `You didn't close ${first} — but you named something ${subject} couldn't deny.`;
  } else {
    headline = `You didn't close ${first}. Let's look at where the room got away.`;
  }

  // Body counts the actual moments. No fabricated "two stood out" when the
  // session produced none.
  let body: string;
  if (wins.length === 0 && growths.length === 0) {
    body = `${first} didn't open up. The conversation stayed too high-level to land. Walk through it with me — we'll find where to anchor in ${possessive} world next time.${priorBit}`;
  } else if (wins.length === 0) {
    body = `${growths.length} moment${growths.length === 1 ? '' : 's'} cost you ground and no clear win to point to yet. Walk through ${growths.length === 1 ? 'it' : 'them'} with me — the next attempt will land differently.${priorBit}`;
  } else {
    const winFrag = `${wins.length} moment${wins.length === 1 ? '' : 's'} landed`;
    const growthFrag = growths.length > 0 ? ` — and ${growths.length} cost you ground` : '';
    body = `${winFrag}${growthFrag}. Walk through them with me, and the next attempt will land differently.${priorBit}`;
  }

  return {
    outcome_label: "DIDN'T CLOSE YET",
    outcome_tone: 'warn',
    score: session.score,
    eyebrow: `${COACH.title} · your debrief`,
    headline,
    body,
  };
}

function priorAttempt(s: SessionRecord, learner: LearnerState): SessionRecord | undefined {
  return learner.session_history.find(
    (x) => x.persona_id === s.persona_id && x.attempt_number === s.attempt_number - 1,
  );
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : `${s.slice(0, n - 1).trimEnd()}…`;
}
